import { useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider, keyframes } from "@mui/material/styles";
import fondo from "../images/fondoAzul.jpg";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";

const logo = `${process.env.PUBLIC_URL}/logo.png`;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Professional Enterprise Theme
const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#001F3F', // Navy Blue - Trust, Authority
    },
    secondary: {
      main: '#3A6B7E', // Teal - Growth, Stability
    },
    background: {
      default: '#F4F6F8', // Light Gray - Cleanliness
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A2027',
      secondary: '#3E5060',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#001F3F',
    },
    body1: {
      color: '#3E5060',
    },
  },
  shape: {
    borderRadius: 16, // Softer corners for modern feel
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: '0 4px 6px rgba(0, 31, 63, 0.1)',
          borderRadius: 12,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 12px rgba(0, 31, 63, 0.2)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          transition: 'all 0.2s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3A6B7E',
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#001F3F',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        }
      }
    }
  },
});

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
      sx={{
        mt: 4,
        opacity: 0.8,
        color: '#fff',
        animation: `${fadeInUp} 0.8s ease-out 0.3s backwards`
      }}
    >
      {"Copyright © "}
      <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontWeight: 'bold' }}>
        IESS
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { signin, isAuthenticated, errors: registerErrors } = useAuth();
  const navigate = useNavigate();

  const onSubmit = handleSubmit((data) => {
    signin(data);
  });

  useEffect(() => {
    if (isAuthenticated) navigate("/inicio/general");
  }, [isAuthenticated]);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: `url(${fondo})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CssBaseline />

        {/* Dark Overlay with Blur */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 31, 63, 0.5)', // Slightly lighter to show off the glass effect
            backdropFilter: 'blur(3px)',
            zIndex: 1,
          }}
        />

        {/* Content Container */}
        <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
          <Paper
            elevation={24}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 5,
              width: '100%',
              // Glassmorphism Styles
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              // Animation
              animation: `${fadeInUp} 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)`,
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 3, height: '80px', display: 'flex', alignItems: 'center' }}>
              <img src={logo} alt="Instituto Ecuatoriano de Seguridad Social" style={{ maxHeight: '100%', maxWidth: '100%' }} />
            </Box>

            <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 700, color: 'primary.main' }}>
              Bienvenido
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}>
              Ingrese sus credenciales para continuar
            </Typography>

            {/* Error Messages */}
            {Array.isArray(registerErrors) && registerErrors.map((error, i) => (
              <Alert severity="error" key={i} sx={{ width: '100%', mb: 2, borderRadius: 2 }}>
                {typeof error === "string" ? error : JSON.stringify(error)}
              </Alert>
            ))}
            {!Array.isArray(registerErrors) && registerErrors && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 2 }}>
                {typeof registerErrors === "string"
                  ? registerErrors
                  : registerErrors.sqlMessage || registerErrors.message || JSON.stringify(registerErrors)}
              </Alert>
            )}

            <Box component="form" noValidate onSubmit={onSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo Electrónico"
                name="usu_email"
                autoComplete="email"
                autoFocus
                {...register("usu_email", { required: true })}
                error={!!errors.email}
                helperText={errors.email && "El correo es requerido"}
              />

              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                required
                error={!!errors.password}
              >
                <InputLabel htmlFor="outlined-adornment-password">Contraseña</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  {...register("usu_password", { required: true })}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Contraseña"
                />
                {errors.password && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    La contraseña es requerida
                  </Typography>
                )}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 4, mb: 3, py: 1.5, fontSize: '1rem' }}
              >
                Iniciar Sesión
              </Button>

              <Grid container justifyContent="center">
                <Grid item>
                  <Link to="/register" style={{ textDecoration: 'none', color: '#3A6B7E', fontWeight: 600, fontSize: '0.9rem' }}>
                    ¿No tiene una cuenta? <span style={{ textDecoration: 'underline' }}>Regístrese aquí</span>
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          <Copyright />
        </Container>
      </Box>
    </ThemeProvider>
  );
}
